// His BRD words the vendored glossary and design-system excerpts do not carry (lane B3, CR-D0-2's fallback). Each
// entry quotes a line of frontend/mc-source/B3-BRD-EXCERPTS.md, copied verbatim from the BRD with its hash in
// B3-SOURCES.json; tests/mc/b3-labels.test.mjs proves the quote is in that excerpt, the text is in the quote and nothing
// is forbidden. These are not registered with the design system's label registry, because its quote check reads only
// the glossary and the design-system excerpts; once D0 vendors BRD §10.3 they move into ./labels.ts. A requirement
// sentence is never standing screen copy (UAT F-32, Q-4's default): it stays in the documents, and here only where a
// blocked control or a refusal names the rule that stops it.

export interface BrdLabel {
  readonly text: string;
  /** The BRD section the excerpt line comes from. */
  readonly source: `BRD §${string}`;
  /** The verbatim excerpt line (or a run of it) the text is taken from. */
  readonly quote: string;
}

const REVIEW_SEVEN =
  "The user must review extracted name, email, phone, location, current employer, employment history, and LinkedIn URL before candidate creation.";
const FILES =
  "Supported V1 resume files include PDF and DOCX; invalid, password-protected, or unreadable files produce a clear error without creating a partial profile.";
const LINKS = "A new requisition application links to the existing Candidate ID rather than duplicating the person profile.";

const ROLES =
  "Recruiter creates/reviews profiles. Authorized Coordinator may enter/upload. TA Admin manages merges/recovery. HRBP view subject to scope.";
const RULES_3_5 =
  "Email is not primary key. Existing candidate links to new requisition without duplication. Resume parsing requires human confirmation. Merge preserves all history.";
const MASTER =
  "One candidate master profile may be linked to multiple requisition-specific applications over time. Each application retains its own stage, status, scorecards, communications, offer outcome, and disposition.";
const EMAIL_RULE = "Email changes do not create a new identity automatically.";
const AUDITED = "Merge, unmerge/recovery, attachment, parsing, and profile-edit actions are fully audited.";

const brd = <const S extends string>(text: S, source: `BRD §${string}`, quote: string) => ({ text, source, quote }) as const;

export const BRD_LABELS = {
  currentEmployer: brd("Current employer"),
  employmentHistory: brd("Employment history"),
  files: brd(FILES),
  links: brd(LINKS),
  // the profile, merge and recovery (lane B3 S12)
  emailIdentity: brd(EMAIL_RULE),
  audited: brd(AUDITED),
  // his TA Admin folds into the one Admin (DC-228): the sentence is drawn from "Admin" on
  adminMerges: brd("Admin manages merges/recovery."),
  mergePreservesHistory: brd("Merge preserves all history."),
  eachApplication: brd(
    "Each application retains its own stage, status, scorecards, communications, offer outcome, and disposition."


),
  requisition: brd("Requisition"),
  stage: brd("Stage"),
} as const satisfies Readonly<Record<string, BrdLabel>>;

/** The texts, as a label module's members are used: `BRD.currentEmployer`. */
export const BRD = Object.freeze(
  Object.fromEntries(Object.entries(BRD_LABELS).map(([key, def]) => [key, def.text])),
) as { readonly [K in keyof typeof BRD_LABELS]: (typeof BRD_LABELS)[K]["text"] };

// ---- the server-rendered card titles (lane B3-minors M1) ------------------------------------------------------------
// Today and the Action Center draw a notification's title as the server wrote it. Two of the candidate master's titles
// are words he has not given yet — each is derived from his sentence below, so each is pending until he names it. The
// string lives once, in backend/app/candidate_items.py; this table mirrors it so the pending status is recorded beside
// his line, and tests/mc/b3-labels.test.mjs fails if the two drift. (The parse card's own title, *Review parsed values*,
// is already a registered pending word: CANDIDATE_LABELS.reviewParsedValues.)

export interface PendingServerTitle {
  readonly text: string;
  readonly source: `BRD §${string}`;
  /** His verbatim excerpt line the title is derived from. */
  readonly quote: string;
  readonly status: "pending";
  /** The backend constant that holds the string (module and name). */
  readonly server: string;
}

const NOTICES = "Create duplicate-review Action Item when potential matches exist. Notify Recruiter when parsing is complete or fails.";

export const SERVER_TITLES = {
  duplicateReview: { text: "Duplicate review", server: "candidate_items.TITLE" },
  parsingFailed: { text: "Parsing failed", server: "candidate_items.PARSE_FAILED_TITLE" },
} as const satisfies Readonly<Record<string, PendingServerTitle>>;
